import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Camera,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Activity,
  Settings,
  Shield,
  BarChart3,
  UserCheck,
  Image,
  CreditCard,
  LogOut,
  CheckCircle,
  Bell,
  Mail
} from 'lucide-react';

interface AdminStats {
  totalUsers: number;
  totalPhotographers: number;
  totalAthletes: number;
  totalPhotos: number;
  totalEvents: number;
  totalRevenue: number;
  pendingApprovals: number;
  activeUsers: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'photographer' | 'athlete';
  status: 'Active' | 'PendingApproval' | 'Rejected' | 'Suspended';
  createdAt: string;
  lastLogin: string;
}

interface Event {
  id: string;
  name: string;
  date: string;
  location: string;
  status: 'active' | 'completed' | 'cancelled';
  photographerCount: number;
  athleteCount: number;
  photoCount: number;
}

export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const { user, logout } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalPhotographers: 0,
    totalAthletes: 0,
    totalPhotos: 0,
    totalEvents: 0,
    totalRevenue: 0,
    pendingApprovals: 0,
    activeUsers: 0
  });
  const [users, setUsers] = useState<User[]>([]);
  const [events, setEvents] = useState<Event[]>([]);

  // Verificar se o usuário é admin
  useEffect(() => {
    if (!user) {
      setLocation('/login');
      return;
    }

    if (user.role !== 'admin') {
      setLocation('/');
      return;
    }

    loadDashboardData();
  }, [user, setLocation]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Simular carregamento de dados
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Dados simulados
      setStats({
        totalUsers: 1247,
        totalPhotographers: 23,
        totalAthletes: 1200,
        totalPhotos: 15420,
        totalEvents: 45,
        totalRevenue: 125430.50,
        pendingApprovals: 8,
        activeUsers: 1156
      });

      setUsers([
        {
          id: '1',
          name: 'Maria Santos',
          email: 'maria@fotup.com',
          role: 'photographer',
          status: 'Active',
          createdAt: '2024-01-15',
          lastLogin: '2024-01-20'
        },
        {
          id: '2',
          name: 'João Silva',
          email: 'joao@fotup.com',
          role: 'athlete',
          status: 'Active',
          createdAt: '2024-01-16',
          lastLogin: '2024-01-20'
        },
        {
          id: '3',
          name: 'Ana Costa',
          email: 'ana@fotup.com',
          role: 'photographer',
          status: 'PendingApproval',
          createdAt: '2024-01-18',
          lastLogin: '2024-01-19'
        }
      ]);

      setEvents([
        {
          id: '1',
          name: 'Campeonato de Futebol 2024',
          date: '2024-02-15',
          location: 'Estádio Municipal',
          status: 'active',
          photographerCount: 5,
          athleteCount: 120,
          photoCount: 450
        },
        {
          id: '2',
          name: 'Maratona da Cidade',
          date: '2024-02-20',
          location: 'Centro da Cidade',
          status: 'active',
          photographerCount: 3,
          athleteCount: 200,
          photoCount: 320
        }
      ]);

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setLocation('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Administrativo</h1>
              <p className="text-sm text-gray-600">Bem-vindo, {user?.name}</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="flex items-center">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="users">Usuários</TabsTrigger>
            <TabsTrigger value="events">Eventos</TabsTrigger>
            <TabsTrigger value="management">Gerenciamento</TabsTrigger>
            <TabsTrigger value="settings">Configurações</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Estatísticas Principais */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">
                    +12% em relação ao mês anterior
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Fotógrafos</CardTitle>
                  <Camera className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalPhotographers}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.pendingApprovals} aguardando aprovação
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Fotos</CardTitle>
                  <Image className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalPhotos.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">
                    +8% em relação ao mês anterior
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">R$ {stats.totalRevenue.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">
                    +15% em relação ao mês anterior
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Gráficos e Tabelas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Atividade Recente</CardTitle>
                  <CardDescription>Últimas ações no sistema</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Novo fotógrafo registrado</p>
                        <p className="text-xs text-muted-foreground">Maria Santos - há 2 horas</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Evento criado</p>
                        <p className="text-xs text-muted-foreground">Campeonato de Futebol - há 4 horas</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Aguardando aprovação</p>
                        <p className="text-xs text-muted-foreground">João Silva - há 6 horas</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Status do Sistema</CardTitle>
                  <CardDescription>Monitoramento em tempo real</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Servidor</span>
                      <Badge variant="default" className="bg-primary">Online</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Banco de Dados</span>
                      <Badge variant="default" className="bg-primary">Online</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Storage</span>
                      <Badge variant="default" className="bg-primary">Online</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">API</span>
                      <Badge variant="default" className="bg-primary">Online</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gerenciamento de Usuários</CardTitle>
                <CardDescription>Visualize e gerencie todos os usuários do sistema</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserCheck className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {user.role === 'admin' ? 'Administrador' :
                              user.role === 'photographer' ? 'Fotógrafo' : 'Atleta'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant={user.status === 'Active' ? 'default' :
                            user.status === 'PendingApproval' ? 'secondary' : 'destructive'}
                        >
                          {user.status === 'Active' ? 'Ativo' :
                            user.status === 'PendingApproval' ? 'Pendente' : 'Suspenso'}
                        </Badge>
                        <Button size="sm" variant="outline">
                          Gerenciar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gerenciamento de Eventos</CardTitle>
                <CardDescription>Visualize e gerencie todos os eventos do sistema</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {events.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Activity className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{event.name}</p>
                          <p className="text-sm text-muted-foreground">{event.location}</p>
                          <p className="text-xs text-muted-foreground">{event.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant={event.status === 'active' ? 'default' : 'secondary'}
                        >
                          {event.status === 'active' ? 'Ativo' : 'Concluído'}
                        </Badge>
                        <div className="text-sm text-muted-foreground">
                          {event.photographerCount} fotógrafos, {event.athleteCount} atletas
                        </div>
                        <Button size="sm" variant="outline">
                          Gerenciar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="management" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gerenciamento do Sistema</CardTitle>
                <CardDescription>Acesse as ferramentas de gerenciamento e administração</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium">Aprovações</p>
                        <p className="text-sm text-muted-foreground">Gerencie aprovações de usuários e eventos</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setLocation('/approvals')}
                    >
                      Acessar
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <Bell className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium">Notificações</p>
                        <p className="text-sm text-muted-foreground">Visualize e gerencie notificações do sistema</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setLocation('/notifications')}
                    >
                      Acessar
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <Mail className="h-5 w-5 text-purple-600" />
                      <div>
                        <p className="font-medium">Gerenciamento de Emails</p>
                        <p className="text-sm text-muted-foreground">Configure SMTP, templates e visualize logs de envio</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setLocation('/admin/emails')}
                    >
                      Acessar
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <Activity className="h-5 w-5 text-orange-600" />
                      <div>
                        <p className="font-medium">Gerenciamento de Eventos</p>
                        <p className="text-sm text-muted-foreground">Gerencie todos os eventos da plataforma</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setLocation('/events/management')}
                    >
                      Acessar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Configurações do Sistema</CardTitle>
                <CardDescription>Gerencie as configurações gerais do sistema</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Settings className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium">Configurações Gerais</p>
                        <p className="text-sm text-muted-foreground">Configurações básicas do sistema</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Configurar
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Shield className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium">Segurança</p>
                        <p className="text-sm text-muted-foreground">Configurações de segurança e permissões</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Configurar
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <BarChart3 className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium">Relatórios</p>
                        <p className="text-sm text-muted-foreground">Configurações de relatórios e analytics</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Configurar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
